import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import { useUser } from '../contexts/UserContext';
import PaywallModal from './PaywallModal';

/**
 * Protected route wrapper that:
 * 1. Redirects to login if not authenticated
 * 2. Shows paywall modal if trial has expired
 */
function ProtectedRoute({ children }) {
  const location = useLocation();
  const { user, loading, canCreateData, isPremium } = useUser();
  const [showPaywall, setShowPaywall] = useState(false);

  // Show paywall when trial expired and not premium
  useEffect(() => {
    if (!loading && !canCreateData && !isPremium) {
      setShowPaywall(true);
    }
  }, [loading, canCreateData, isPremium]);

  // Not authenticated - redirect to login
  if (!api.isAuthenticated()) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Still loading user data
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-primary mb-4"></i>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Allow access to account page even if trial expired (for export/delete)
  const isAccountPage = location.pathname === '/account';
  const isUpgradePage = location.pathname === '/upgrade';

  return (
    <>
      {children}
      {/* Show paywall for non-account pages when trial expired */}
      {showPaywall && !isAccountPage && !isUpgradePage && (
        <PaywallModal
          isOpen={showPaywall}
          onClose={() => setShowPaywall(false)}
          user={user}
        />
      )}
    </>
  );
}

export default ProtectedRoute;
